import { Link } from "@tanstack/react-router";
import type { ReactElement } from "react";
import type { AlwaysAiUser } from "../../../../shared/api-types";
import {
  PatientRedFlagBanner,
  PatientRedFlagReadFailed,
  type RenderAppointmentLink,
} from "@/components/v2/patient-red-flag-banner";
import { redFlagBannerView, redFlagSectionState } from "@/lib/patient-red-flags-client";
import { can } from "@/lib/permissions";
import { useMarkRedFlagCalled, usePatientRedFlags } from "@/lib/use-patient-red-flags";

// The red-flag slot under the patient page header (NO-850). It asks for the
// Client's open flag only when the viewer may read their own Clients' flags,
// and shows Mark called only to a viewer who may record it. Loading and "no
// open flag" render nothing; a failed read renders a neutral line.

const renderAppointmentLink: RenderAppointmentLink = ({
  appointmentId,
  className,
  children,
}) => (
  <Link
    to="/appointments/$appointmentId"
    params={{ appointmentId }}
    search={{}}
    className={className}
  >
    {children}
  </Link>
);

/** `viewer` is the page's signed-in user, undefined while it loads. */
export function PatientRedFlagSection({
  patientId,
  viewer,
}: {
  patientId: string;
  viewer: AlwaysAiUser | undefined;
}): ReactElement | null {
  const canRead = viewer ? can(viewer, "patient_red_flags:read:own") : false;
  const canMark = viewer ? can(viewer, "patient_red_flags:write:own") : false;
  const read = usePatientRedFlags(patientId, canRead);
  const { markCalled, marking, outcome } = useMarkRedFlagCalled(patientId);
  const state = redFlagSectionState(read);

  if (!canRead || state === "nothing") return null;

  if (state === "failed") {
    return (
      <div className="mt-6">
        <PatientRedFlagReadFailed onRetry={() => void read.refetch()} />
      </div>
    );
  }

  const view = redFlagBannerView(read.data?.openFlag ?? null);
  if (!view) return null;

  return (
    <div className="mt-6">
      <PatientRedFlagBanner
        flagText={view.flagText}
        appointmentId={view.appointmentId}
        renderAppointmentLink={renderAppointmentLink}
        onMarkCalled={canMark ? () => markCalled(view.request) : undefined}
        marking={marking}
        markFailed={outcome === "failed"}
      />
    </div>
  );
}
